import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TablePagination,
  Tab,
} from "@mui/material";

import {
  ButtonV1,
  Loader,
  PaginationAction,
  TooltipLabel,
  HyperLink,
  StatusChip,
  ModalBox,
  PriorityStatusChip,
} from "components";
import {
  Filters,
  LabelValueList,
  TooltipView,
  TooltipLabelView,
  EditableView,
} from "./components";
import { ErrorView, QuickFilter } from "../components";
import PickUpDetailsModal from "./components/PickUpDetailsModal";

import { useToaster } from "hooks";
import { formatDate, moneyFormat, validateNull } from "utils";
import {
  getFetchDOReleasePayload,
  getFormattedCreditRequest,
  getFormattedPaymentRequest,
  formatSourceSellerData,
  getStatusData,
  formatSellerInfo,
  formatPickUpDetails,
  formatBuyerInfo,
} from "./DataModel";
import { SellerJODLHoverBlock } from "./components/SellerJODLHoverBlock";
import { postCall_v2 } from "services";

import { error, success, JOMS_API_BASE_V1_URL } from "constants";
import {
  DO_RELEASE_QUICK_FILTERS,
  DO_RELEASE_HEADERS,
  PICKUP_DETAILS_UPDATE_SUCCESS_MSG,
} from "../constant";

const rowsPerPage = 50;

const DoReleaseTab = () => {
  const [doReleaseData, setDoReleaseData] = useState({});
  const [filters, setFilters] = useState({});
  const [selectedQuickFilter, setSelectedQuickFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [toolTipData, setToolTipData] = useState({});
  const [isPickModelOpen, setIsPickModelOpen] = useState(false);
  const [selectedPreData, setSelectedPreData] = useState({});

  const triggerToaster = useToaster();
  const theme = useTheme();

  const fetchDoReleaseData = async () => {
    try {
      const { data } = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/fetch-suggested-DO-data`,
        getFetchDOReleasePayload(
          page,
          rowsPerPage,
          filters,
          selectedQuickFilter,
        ),
      );

      if (data) {
        setDoReleaseData(data);
      }
    } catch (err) {
      triggerToaster(err?.data?.errorMessage || "Something went wrong!", error);
      console.error("Error fetching do release:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchDoReleaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, selectedQuickFilter]);

  const handlePageChange = useCallback(
    (_event, pageNumber) => setPage(pageNumber),
    [],
  );

  const handleFilterSubmit = (filterData) => {
    setFilters(filterData);
  };

  const handleQuickFilter = (selectedFilter) => {
    setSelectedQuickFilter(selectedFilter);
  };

  const handleRefresh = async (doId) => {
    try {
      await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/refresh/${doId}`,
      );
      fetchDoReleaseData();
      triggerToaster("Successfully refreshed details", success);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const doApproval = async (itemDetails, action) => {
    try {
      const actionMapper = {
        APPROVE: "APPROVED",
        REJECT: "REJECTED",
      };
      const payload = {
        preDoReleaseRtabId: itemDetails.id,
        preDoReleaseRequestStatus: actionMapper[action],
      };
      await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/change-status`,
        payload,
      );
      triggerToaster(
        `Status changed to  ${actionMapper[
          action
        ]?.toLowerCase()} successfully.`,
        success,
      );
      fetchDoReleaseData();
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const { Released, Rejected, Approved } = DO_RELEASE_QUICK_FILTERS;

  const handlePaymentToolTip = async (id) => {
    try {
      const payload = {
        preDoId: id,
        doUpdateRequest: [],
      };

      const { data } = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/fetch-pre-do-balance-data`,
        payload,
      );
      setToolTipData(data);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const handleReset = () => {
    setSelectedQuickFilter("All");
  };

  const updateRow = (paymentData) => {
    if (!paymentData?.preDoId) return;

    const updatedTableData = doReleaseData?.data?.map((row) =>
      row.id === paymentData.preDoId
        ? {
            ...row,
            doCashValue: paymentData.payment,
            doCreditValue: paymentData.credit,
          }
        : row,
    );

    setDoReleaseData((prev) => ({
      ...prev,
      data: updatedTableData,
    }));
  };

  const handleEditDoQty = async (value, lineItemId, doId) => {
    try {
      const payload = {
        preDoId: doId,
        doUpdateRequest: [
          {
            lineItemId: lineItemId,
            updatedQty: +value,
          },
        ],
      };

      const { data } = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/edit-do-quantity-payment-detail`,
        payload,
      );

      updateRow(data);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const handleSavePickUpDetails = async (payload) => {
    try {
      const res = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/seller-and-pickup-details`,
        payload,
      );
      fetchDoReleaseData();
      if (res?.data) {
        triggerToaster(PICKUP_DETAILS_UPDATE_SUCCESS_MSG, success);
        setIsPickModelOpen(false);
      }
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const {
    common,
    bgColor,
    error: { dark },
  } = theme.palette;

  const handlePickUpDetails = (preDoData) => {
    setSelectedPreData(preDoData);
    setIsPickModelOpen(true);
  };

  const handlePickUpModelClose = () => {
    setIsPickModelOpen(false);
  };

  const getFgReadinessDate = (dateString) => {
    if (!dateString) {
      return (
        <TableCell
          sx={{
            color: theme.palette.success.dark,
            fontWeight: "600",
            fontSize: 14,
          }}
        >
          FG ready
        </TableCell>
      );
    }
    return (
      <TableCell
        sx={{
          color: dark,
          fontSize: 14,
        }}
      >
        {formatDate(dateString)}
      </TableCell>
    );
  };

  return (
    <Box>
      <Box mb={4}>
        <Filters
          onFilterSubmit={handleFilterSubmit}
          onResetFilter={handleReset}
        />
      </Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <QuickFilter
          onQuickFilter={handleQuickFilter}
          state={selectedQuickFilter}
          filterData={DO_RELEASE_QUICK_FILTERS}
        />
      </Box>
      <Box mt={4}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {DO_RELEASE_HEADERS.map((header) => (
                  <TableCell key={header}>
                    <Typography fontSize={14} fontWeight="bold">
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={DO_RELEASE_HEADERS.length}>
                    <Loader />
                  </TableCell>
                </TableRow>
              )}
              {((!isLoading && doReleaseData?.data) || []).map((row, index) => {
                const errors = row?.validationErrorMsg || [];
                const doLineItems = row?.lineItemDetailsList;

                const background = index % 2 === 0 ? common?.white : bgColor;
                const status = row?.preDoReleaseStatus?.toLowerCase();

                const isApproveRejectRefreshAction = [
                  Released?.toLowerCase(),
                  Rejected?.toLowerCase(),
                  Approved?.toLowerCase(),
                ].includes(row?.preDoReleaseStatus?.toLowerCase());

                const isApproveDisabled =
                  +row?.doCashValue > 0 || +row?.doCreditValue > 0;

                const paymentRequestBreakup =
                  toolTipData?.paymentRequestBreakupDTO || {};

                const creditRequestBreakup =
                  toolTipData?.creditRequestBreakupDTO || {};

                const isApproveButtonDisabled =
                  row?.deliveryType === "JOTS" &&
                  (!row?.pickupSlotFrom || !row?.pickupSlotTo);

                return (
                  <>
                    <TableRow
                      key={index}
                      style={{ backgroundColor: background }}
                    >
                      <TableCell>
                        <TooltipLabel
                          customStyle={{ width: 300 }}
                          placement="right"
                          title={
                            row?.customerOrderId && (
                              <SellerJODLHoverBlock
                                sourceSellerData={formatSourceSellerData(
                                  row?.sourceSeller,
                                )}
                                jodlData={formatSellerInfo(row?.sellerDetails)}
                                pickupData={formatPickUpDetails(
                                  row?.pickupSlotFrom,
                                  row?.pickupSlotTo,
                                  row?.pickupLocation,
                                  row?.pickupAddress,
                                )}
                                buyerData={formatBuyerInfo(
                                  row?.customerName,
                                  row?.buyerGstin,
                                )}
                              />
                            )
                          }
                          labelChildren={
                            <Typography
                              sx={{
                                cursor: "pointer",
                                color: theme.palette.primary?.main,
                                fontSize: 14,
                              }}
                            >
                              <HyperLink title={row?.customerOrderId} />
                            </Typography>
                          }
                        />
                      </TableCell>
                      <TableCell>{`${row?.customerName ?? ""}`}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>
                        <PriorityStatusChip priority={row?.orderPriority} />
                      </TableCell>
                      <TableCell>
                        {`${row?.fgQuantity} ${row?.measurementUnit ?? ""}`}
                      </TableCell>
                      <TableCell>
                        {`${row?.doQuantity} ${row?.measurementUnit ?? ""}`}
                      </TableCell>
                      <TableCell>
                        <TooltipLabelView
                          label={moneyFormat(row?.doCashValue)}
                          type="Payment Request"
                          onTooltipOpen={() => handlePaymentToolTip(row?.id)}
                          tooltipContent={
                            <LabelValueList
                              layout="column"
                              items={getFormattedPaymentRequest(
                                paymentRequestBreakup,
                              )}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TooltipLabelView
                          label={moneyFormat(row?.doCreditValue)}
                          onTooltipOpen={() => handlePaymentToolTip(row?.id)}
                          tooltipContent={
                            <LabelValueList
                              layout="column"
                              items={getFormattedCreditRequest(
                                creditRequestBreakup,
                              )}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell> {validateNull(row?.orderType)}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>
                        {status ? (
                          <StatusChip
                            type={getStatusData(status)?.type}
                            label={getStatusData(status)?.label}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {getFgReadinessDate(row?.expectedFgDate)}
                      </TableCell>
                      <TableCell>
                        {!isApproveRejectRefreshAction ? (
                          <Box>
                            <Box
                              sx={{ display: "flex", flexDirection: "row" }}
                              gap={2}
                            >
                              <ButtonV1
                                style={{
                                  color: dark,
                                  border: `1px solid ${dark}`,
                                  "&:hover": {
                                    border: `1px solid ${dark}`,
                                  },
                                }}
                                variant="outlined"
                                title="Reject"
                                color="error"
                                size="small"
                                onClick={() => doApproval(row, "REJECT")}
                              />
                              <ButtonV1
                                title="Approve"
                                size="small"
                                disabled={
                                  isApproveDisabled || isApproveButtonDisabled
                                }
                                onClick={() => doApproval(row, "APPROVE")}
                              />
                              <ButtonV1
                                variant="contained"
                                title="Refresh"
                                size="small"
                                onClick={() => {
                                  handleRefresh(row.id);
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 2,
                              }}
                            >
                              <ButtonV1
                                variant="outlined"
                                title="Add Pickup Location"
                                size="small"
                                onClick={() => {
                                  handlePickUpDetails(row);
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    {doLineItems?.length &&
                      doLineItems?.map((item) => {
                        const tooltipData = {
                          totalDoReleasedQuantity:
                            item?.totalDoReleasedQuantity || 0,
                          pendingDoQuantity: item?.pendingDoQuantity || 0,
                          dispatchQuantity: item?.totalShippedQuantity || 0,
                          totalOrderedQuantity: row?.totalOrderedQuantity || 0,
                          measurementUnit: row?.measurementUnit,
                        };

                        return (
                          <TableRow style={{ backgroundColor: background }}>
                            <TableCell colSpan={2} />
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 3,
                                }}
                              >
                                <Typography>
                                  {validateNull(item?.sapSkuProduct)}
                                </Typography>
                                <Typography>
                                  {validateNull(item?.soNumber)}
                                </Typography>
                                <Typography>
                                  {item?.soLineItemNumbers?.length > 0
                                    ? `[${item.soLineItemNumbers.join(", ")}]`
                                    : "-"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {validateNull(item?.erpSkuName)}
                            </TableCell>
                            <TableCell />
                            <TableCell>
                              <TooltipLabel
                                customStyle={{ width: 400 }}
                                placement="bottom"
                                title={
                                  item.sapSkuProduct && (
                                    <TooltipView data={tooltipData} />
                                  )
                                }
                                labelChildren={
                                  <Typography
                                    sx={{
                                      cursor: "pointer",
                                      color: theme.palette.primary?.main,
                                      fontSize: 14,
                                    }}
                                  >
                                    {`${item?.lineItemFgQuantity || 0} ${
                                      item?.measurementUnit ?? ""
                                    }`}
                                  </Typography>
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <EditableView
                                lineItemData={item}
                                doId={row.id}
                                onEditDoQty={handleEditDoQty}
                                isApproveRejectRefreshAction={
                                  isApproveRejectRefreshAction
                                }
                              />
                            </TableCell>
                            <TableCell colSpan={3} />
                            <TableCell>
                              {validateNull(item?.materialShortDescription)}
                            </TableCell>
                            <TableCell>
                              {moneyFormat(validateNull(item?.rate))}
                            </TableCell>
                            <TableCell colSpan={3} />
                          </TableRow>
                        );
                      })}
                    <ErrorView
                      errors={errors}
                      warning={row?.validationWarningMsg ?? ""}
                      colSpan={DO_RELEASE_HEADERS.length}
                    />
                  </>
                );
              })}
              {!isLoading && !doReleaseData?.data?.length && (
                <TableRow>
                  <TableCell colSpan={DO_RELEASE_HEADERS.length}>
                    <Typography textAlign={"center"}>No data found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!!doReleaseData && (
            <TablePagination
              rowsPerPageOptions={[rowsPerPage]}
              rowsPerPage={rowsPerPage}
              page={Math.ceil(doReleaseData?.offset / rowsPerPage) ?? 0}
              count={doReleaseData?.count ?? 0}
              onPageChange={handlePageChange}
              sx={{ display: "flex", flexDirection: "column-reverse" }}
              ActionsComponent={PaginationAction}
            />
          )}
        </TableContainer>
      </Box>
      <ModalBox
        onCloseModal={handlePickUpModelClose}
        open={isPickModelOpen}
        width="70%"
      >
        <PickUpDetailsModal
          selectedPreData={selectedPreData}
          onCloseModal={handlePickUpModelClose}
          onSavePickUpDetails={handleSavePickUpDetails}
        />
      </ModalBox>
    </Box>
  );
};

export default DoReleaseTab;
